import { Chip, ChipProps, SxProps, Theme } from '@mui/material'
import { FC, MouseEventHandler } from 'react'

/**
 * @designSystem
 */
export type C1ChipProps = Omit<ChipProps, 'label' | 'size'> & {
    /** Chip content text or element. Required. */
    label: NonNullable<React.ReactNode>
    /** Chip size. `extraSmall` is C1-specific (10px font, 16px height) — useful for inline status badges. */
    size?: 'small' | 'medium' | 'extraSmall'
    /** Chip color theme. Use to convey semantic meaning: `success` for active/granted, `error` for deleted/failed, `warning` for expiring/pending, `info` for neutral metadata, `primary` for branded tags. */
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
    /** Chip visual style. `filled` (default) has a solid background; `outlined` has a border with transparent background. */
    variant?: 'filled' | 'outlined'
    /** When provided, the chip becomes clickable with hover/focus styles. Use for filter chips or toggleable tags. */
    onClick?: MouseEventHandler<HTMLDivElement>
    /** When provided, renders a delete icon (×). Callback fires on delete icon click. Use for removable filter tags. */
    onDelete?: ChipProps['onDelete']
    /** Icon element rendered before the label. Use `<C1Icon icon={...} />` for Lucide icons. */
    icon?: React.ReactElement
    /** MUI system styles merged onto the root chip element. */
    sx?: SxProps<Theme>
}

export const extraSmallChipSx = { fontSize: '10px', height: '16px', ml: '6px' }

/**
 * Compact element for status badges, tags, filters, or small pieces of information.
 *
 * Wraps MUI Chip with a C1-specific `extraSmall` size and a required `label` prop
 * (MUI's label is optional, but a chip without content is meaningless).
 *
 * ## Sizes
 * - `medium` — default MUI size, standard height
 * - `small` — reduced height for denser layouts (table cells, lists)
 * - `extraSmall` — C1-specific: 10px font, 16px height. Use for compact inline badges
 *   next to headings or display names (e.g. "Admin", "Deleted")
 *
 * ## Colors
 * - `default` — no semantic meaning, neutral grey
 * - `primary` — branded tags, structural labels (e.g. "if"/"then" in policy rules)
 * - `success` — positive status (active, granted, configured, approved)
 * - `warning` — caution status (expiring, pending, anomaly detected)
 * - `error` — negative status (deleted, failed, expired)
 * - `info` — neutral metadata (expression values, ticket status, filter tags)
 *
 * ## Variants
 * - `filled` (default) — solid background, higher visual weight
 * - `outlined` — border-only, lighter appearance for secondary context
 *
 * ## Behavior
 * - `extraSmall` maps to MUI's `small` internally and applies custom `fontSize`/`height` via sx
 * - When `extraSmall` is used, any `sx` prop you pass is **overridden** by the extraSmall styles
 * - Pass `onClick` to make the chip interactive (hover/focus styles added automatically)
 * - Pass `onDelete` to show a dismiss icon (×) at the end
 *
 * @designSystem
 * @category atoms
 * @externalReference MUI Chip API | https://mui.com/material-ui/api/chip/
 */
export const C1Chip: FC<C1ChipProps> = (props) => {
    const { size, ...rest } = props
    const chipSize = size === 'extraSmall' ? 'small' : size
    const chipSx = size === 'extraSmall' ? extraSmallChipSx : rest.sx

    return <Chip {...rest} size={chipSize} sx={chipSx} />
}
